export type CreditCoverageReason =
  | "not_enrolled"
  | "unknown"
  | "no_charge"
  | "overage"
  | "session_paid"
  | "session_in_quota"
  | "no_price"
  | "insufficient"
  | "balance"
  | "unavailable";

export interface CreditCoverage {
  covers: boolean;
  exhausted: boolean;
  refused: boolean;
  reason: CreditCoverageReason;
  enrolled: boolean;
  balanceCop: number | null;
  priceCop: number | null;
  pendingCharges: number;
  owedCop: number;
}

const REASONS: CreditCoverageReason[] = [
  "not_enrolled",
  "unknown",
  "no_charge",
  "overage",
  "session_paid",
  "session_in_quota",
  "no_price",
  "insufficient",
  "balance",
];

const COVERING: CreditCoverageReason[] = ["balance", "session_paid", "session_in_quota"];

export const CREDIT_COVERAGE_UNAVAILABLE: CreditCoverage = {
  covers: false,
  exhausted: false,
  refused: false,
  reason: "unavailable",
  enrolled: false,
  balanceCop: null,
  priceCop: null,
  pendingCharges: 0,
  owedCop: 0,
};

function numOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function parseCreditCoverage(raw: unknown): CreditCoverage {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return CREDIT_COVERAGE_UNAVAILABLE;
  const row = raw as Record<string, unknown>;
  const reason = REASONS.find((candidate) => candidate === row.reason);
  if (!reason) return CREDIT_COVERAGE_UNAVAILABLE;
  const enrolled = row.enrolled === true;
  const covers = row.covers === true && COVERING.includes(reason);
  const exhausted = enrolled && row.exhausted === true && reason === "insufficient";
  return {
    covers,
    exhausted,
    refused: enrolled && !covers && (exhausted || reason === "no_price"),
    reason,
    enrolled,
    balanceCop: numOrNull(row.balance_cop),
    priceCop: numOrNull(row.price_cop),
    pendingCharges: numOrNull(row.pending_charges) ?? 0,
    owedCop: numOrNull(row.owed_cop) ?? 0,
  };
}

// deno-lint-ignore no-explicit-any
type Db = any;

async function callCoverage(sb: Db, fn: string, orgId: string, conversationId: string | null): Promise<CreditCoverage> {
  try {
    const { data, error } = await sb.rpc(fn, { p_org: orgId, p_conversation_id: conversationId });
    if (error) {
      console.error(`[credits-coverage] ${fn} ${orgId} failed:`, String(error.message ?? ""));
      return CREDIT_COVERAGE_UNAVAILABLE;
    }
    return parseCreditCoverage(data);
  } catch (err) {
    console.error(`[credits-coverage] ${fn} ${orgId} failed:`, err instanceof Error ? err.message : String(err));
    return CREDIT_COVERAGE_UNAVAILABLE;
  }
}

export function creditBalanceCoverage(sb: Db, orgId: string, conversationId: string | null = null): Promise<CreditCoverage> {
  return callCoverage(sb, "credit_balance_covers", orgId, conversationId);
}

export function creditPayConversation(sb: Db, orgId: string, conversationId: string): Promise<CreditCoverage> {
  return callCoverage(sb, "credit_pay_conversation", orgId, conversationId);
}

export function creditConversationCoverage(sb: Db, orgId: string, conversationId: string): Promise<CreditCoverage> {
  return callCoverage(sb, "credit_conversation_coverage", orgId, conversationId);
}

export async function creditsEnrolled(sb: Db, orgId: string): Promise<boolean | null> {
  try {
    const { data, error } = await sb
      .from("credit_balances")
      .select("organization_id")
      .eq("organization_id", orgId)
      .maybeSingle();
    if (error) {
      console.error(`[credits-coverage] enrollment of ${orgId} unreadable:`, String(error.message ?? ""));
      return null;
    }
    return !!data;
  } catch (err) {
    console.error(`[credits-coverage] enrollment of ${orgId} unreadable:`, err instanceof Error ? err.message : String(err));
    return null;
  }
}

export async function failClosedCoverage(sb: Db, orgId: string, coverage: CreditCoverage): Promise<CreditCoverage> {
  if (coverage.reason !== "unavailable") return coverage;
  if ((await creditsEnrolled(sb, orgId)) !== true) return coverage;
  return { ...coverage, enrolled: true, refused: true };
}
